"use client"

import type { ComponentProps, MouseEvent, ReactNode } from "react"

import Link from "next/link"

import useAnalytics from "@/hooks/useAnalytics"

// Utility type to extract props for either Link or 'a'
type LinkOrAnchorComponent = "a" | typeof Link
type LinkOrAnchorProps<T extends LinkOrAnchorComponent> = T extends typeof Link
  ? Omit<ComponentProps<typeof Link>, "href"> & { href: string }
  : Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, "href"> & {
      href: string
    }

type TrackedLinkBaseProps = {
  action: string
  category: string
  children?: ReactNode
  label?: string
}

type TrackedLinkProps<T extends LinkOrAnchorComponent = typeof Link> =
  LinkOrAnchorProps<T> &
    TrackedLinkBaseProps & {
      component?: T
      value?: string
    }

export default function TrackedLink<
  T extends LinkOrAnchorComponent = typeof Link
>({
  action,
  category,
  children,
  component: Component,
  href,
  label,
  onClick,
  value,
  ...rest
}: TrackedLinkProps<T>) {
  const { trackEvent } = useAnalytics()
  const Comp = Component ?? Link

  // onClick handler for both Link and <a>
  function handleClick(e: MouseEvent<HTMLAnchorElement>) {
    onClick?.(e)

    // Get the actual text content from the clicked element
    // e.currentTarget is always the anchor element that the event listener is attached to
    const textContent = e.currentTarget.textContent?.trim() || ""

    void trackEvent({
      action,
      category,
      label: label || textContent || "Link",
      value: value || href
    })
  }

  return (
    <Comp {...rest} href={href} onClick={handleClick}>
      {children}
    </Comp>
  )
}
